import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  inject,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { IconColor, IconModule } from '@freelancer/ui/icon';
import { Subscription } from 'rxjs';
import { CardListSelectColor, CardListSize } from './card-list.types';

@Component({
  standalone: true,
  imports: [IconModule],
  selector: 'fl-card-list-item',
  template: `
    <div
      class="CardListItem"
      [class.IsSelected]="selectable && isSelected"
      [attr.aria-selected]="selectable ? isSelected : undefined"
      [attr.data-selectable]="selectable && !disabled"
      [attr.data-color]="color"
      [attr.role]="selectable ? 'option' : undefined"
      (click)="onClick()"
    >
      @if (iconName) {
        <fl-icon
          [name]="iconName"
          [color]="IconColor.INHERIT"
        ></fl-icon>
      }

      <p class="text-color-inherit text-xxsmall text-center">
        <ng-content></ng-content>
      </p>
    </div>
  `,
  styleUrls: ['./card-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardListItemComponent<T = boolean>
  implements OnChanges, OnInit, OnDestroy
{
  changeDetectorRef = inject(ChangeDetectorRef);

  IconColor = IconColor;

  public color = CardListSelectColor.DEFAULT;
  public selectable = false;

  isSelected = false;

  @HostBinding('attr.data-size')
  public size = CardListSize.MID;

  @RepetitiveSubscription()
  private controlSubscription?: Subscription;

  @Input() control?: FormControl<
    [T] extends [undefined] ? boolean : T | undefined
  >;
  @Input() disabled = false;
  @Input() iconName?: string;
  /** If this is set, the control will be treated like a radio (set to value instead of `true`/`false`) */
  @Input() radioValue?: T;

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.controlSubscription?.unsubscribe();
      this.controlSubscription = this.control?.valueChanges.subscribe(() => {
        this.updateSelectedState();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  ngOnInit(): void {
    this.updateSelectedState();
  }

  onClick(): void {
    if (this.control && this.control.enabled && !this.disabled) {
      // If we're given a radioValue, we treat the control like a radio
      if (this.radioValue !== undefined && this.isRadioControl(this.control)) {
        this.control.setValue(this.radioValue);
      } else if (this.isCheckboxControl(this.control)) {
        this.control.setValue(!this.control.value);
      }
    }
  }

  updateSelectedState(): void {
    // If we're given a radioValue, we treat the control like a radio
    if (this.control) {
      if (this.radioValue !== undefined && this.isRadioControl(this.control)) {
        this.isSelected = this.control.value === this.radioValue;
      } else if (this.isCheckboxControl(this.control)) {
        this.isSelected = this.control.value;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.controlSubscription) {
      this.controlSubscription.unsubscribe();
    }
  }

  private isCheckboxControl(
    control: AbstractControl,
  ): control is FormControl<boolean> {
    return this.radioValue === undefined;
  }

  private isRadioControl(control: AbstractControl): control is FormControl<T> {
    return this.radioValue !== undefined;
  }
}
